import React, { useState, useEffect } from 'react';
import {
    X, Bell, AlertTriangle, Pin, Users, UserCheck,
    Calendar, Clock, Link as LinkIcon, Paperclip,
    Trash2, Upload, Eye
} from 'lucide-react';
import api from '../../utils/api';
import RichTextEditor from '../RichTextEditor';

const NoticeCreationModal = ({ isOpen, onClose, onSuccess, notice, phase }) => {
    const [formData, setFormData] = useState({
        title: '',
        content: '',
        priority: 'normal',
        assignment_type: 'all',
        assigned_roles: [],
        assigned_employee_ids: [],
        show_popup_on_login: false,
        external_link: '',
        expiry_date: '',
        scheduled_date: '',
        attachments: []
    });

    const [employees, setEmployees] = useState([]);
    const [roles, setRoles] = useState([]);
    const [loading, setLoading] = useState(false);
    const [uploadingFile, setUploadingFile] = useState(false);
    const [activeSection, setActiveSection] = useState('content'); // content, targeting, settings

    const isEditing = Boolean(notice);

    useEffect(() => {
        if (isOpen) {
            fetchEmployees();
            fetchRoles();

            if (notice) {
                setFormData({
                    title: notice.title || '',
                    content: notice.content || '',
                    priority: notice.priority || 'normal',
                    phase: notice.phase || phase || 'onboarding',
                    assignment_type: notice.assignment_type || 'all',
                    assigned_roles: notice.assigned_roles || [],
                    assigned_employee_ids: notice.assigned_employee_ids || [],
                    show_popup_on_login: notice.show_popup_on_login || false,
                    external_link: notice.external_link || '',
                    expiry_date: notice.expiry_date ? notice.expiry_date.split('T')[0] : '',
                    scheduled_date: notice.scheduled_date ? notice.scheduled_date.split('T')[0] : '',
                    attachments: notice.attachments || []
                });
            } else {
                setFormData({
                    title: '',
                    content: '',
                    priority: 'normal',
                    phase: phase || 'onboarding',
                    assignment_type: 'all',
                    assigned_roles: [],
                    assigned_employee_ids: [],
                    show_popup_on_login: false,
                    external_link: '',
                    expiry_date: '',
                    scheduled_date: '',
                    attachments: []
                });
            }
        }
    }, [isOpen, notice, phase]);

    const fetchEmployees = async () => {
        try {
            const response = await api.get('/notices/company-employees');
            setEmployees(response.data.employees || []);
        } catch (error) {
            console.error('Error fetching employees:', error);
        }
    };

    const fetchRoles = async () => {
        try {
            const response = await api.get('/notices/company-roles');
            setRoles(response.data.roles || []);
        } catch (error) {
            console.error('Error fetching roles:', error);
        }
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleContentChange = (content) => {
        setFormData(prev => ({ ...prev, content }));
    };

    const handleRoleToggle = (role) => {
        setFormData(prev => ({
            ...prev,
            assigned_roles: prev.assigned_roles.includes(role)
                ? prev.assigned_roles.filter(r => r !== role)
                : [...prev.assigned_roles, role]
        }));
    };

    const handleEmployeeToggle = (employeeId) => {
        setFormData(prev => ({
            ...prev,
            assigned_employee_ids: prev.assigned_employee_ids.includes(employeeId)
                ? prev.assigned_employee_ids.filter(id => id !== employeeId)
                : [...prev.assigned_employee_ids, employeeId]
        }));
    };

    const handleFileUpload = async (e) => {
        const files = Array.from(e.target.files);
        if (files.length === 0) return;

        setUploadingFile(true);
        try {
            for (const file of files) {
                const formDataUpload = new FormData();
                formDataUpload.append('file', file);
                formDataUpload.append('category', 'notice-attachment');

                const response = await api.post('/files/upload', formDataUpload, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                });

                if (response.data.success) {
                    const newAttachment = {
                        file_name: response.data.file.file_name,
                        file_url: response.data.file.file_url,
                        file_key: response.data.file.file_key,
                        file_size: response.data.file.file_size,
                        file_type: response.data.file.file_type
                    };

                    setFormData(prev => ({
                        ...prev,
                        attachments: [...prev.attachments, newAttachment]
                    }));
                }
            }
        } catch (error) {
            console.error('Error uploading file:', error);
            alert('Failed to upload file');
        } finally {
            setUploadingFile(false);
        }
    };

    const handleRemoveAttachment = (index) => {
        setFormData(prev => ({
            ...prev,
            attachments: prev.attachments.filter((_, i) => i !== index)
        }));
    };

    const handleSubmit = async (status = 'draft') => {
        if (!formData.title.trim()) {
            alert('Please enter a notice title');
            return;
        }

        if (!formData.content.trim()) {
            alert('Please enter notice content');
            return;
        }

        setLoading(true);
        try {
            const payload = {
                ...formData,
                phase: formData.phase,
                status,
                scheduled_date: formData.scheduled_date || null,
                expiry_date: formData.expiry_date || null,
                external_link: formData.external_link || null
            };

            if (isEditing) {
                await api.put(`/notices/${notice.id}`, payload);
            } else {
                await api.post('/notices', payload);
            }

            onSuccess();
        } catch (error) {
            console.error('Error saving notice:', error);
            alert('Failed to save notice');
        } finally {
            setLoading(false);
        }
    };

    const handleSchedule = async () => {
        if (!formData.scheduled_date) {
            alert('Please select a scheduled date');
            return;
        }
        await handleSubmit('scheduled');
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-0 md:p-4 transition-all duration-300">
            <div className="bg-card w-full h-full md:h-auto md:max-h-[90vh] md:max-w-4xl md:rounded-2xl shadow-xl overflow-hidden flex flex-col animate-in fade-in zoom-in-95 duration-200">
                {/* Header */}
                <div className="px-4 py-3 md:px-6 md:py-4 border-b border-border flex items-center justify-between bg-primary safe-area-top">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-card/20 rounded-lg">
                            <Bell className="w-5 h-5 text-white" />
                        </div>
                        <div>
                            <h2 className="text-lg md:text-xl font-bold text-white">
                                {isEditing ? 'Edit Notice' : 'Create Notice'}
                            </h2>
                            <p className="text-white/90 text-xs md:text-sm capitalize">{phase} Phase</p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 hover:bg-card/20 rounded-lg transition-colors"
                    >
                        <X className="w-5 h-5 text-white" />
                    </button>
                </div>

                {/* Section Tabs */}
                <div className="px-4 py-2 md:px-6 border-b border-border bg-muted overflow-x-auto">
                    <div className="flex gap-2 md:gap-4">
                        {['content', 'targeting', 'settings'].map((section) => (
                            <button
                                key={section}
                                onClick={() => setActiveSection(section)}
                                className={`px-3 py-1.5 md:px-4 md:py-2 text-sm font-medium rounded-lg transition-colors whitespace-nowrap ${activeSection === section
                                    ? 'bg-primary text-primary-foreground'
                                    : 'text-muted-foreground hover:bg-muted'
                                    }`}
                            >
                                {section.charAt(0).toUpperCase() + section.slice(1)}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-4 md:p-6">
                    {/* Content Section */}
                    {activeSection === 'content' && (
                        <div className="space-y-6">
                            {/* Title */}
                            <div>
                                <label className="block text-sm font-medium text-foreground mb-2">
                                    Notice Title *
                                </label>
                                <input
                                    type="text"
                                    name="title"
                                    value={formData.title}
                                    onChange={handleInputChange}
                                    placeholder="Enter notice title..."
                                    className="w-full px-4 py-3 border border-border rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent"
                                />
                            </div>

                            {/* Priority */}
                            <div>
                                <label className="block text-sm font-medium text-foreground mb-2">
                                    Priority
                                </label>
                                <div className="flex gap-3">
                                    {[
                                        { value: 'normal', label: 'Normal', icon: Bell, color: 'blue' },
                                        { value: 'pinned', label: 'Pinned', icon: Pin, color: 'amber' },
                                        { value: 'urgent', label: 'Urgent', icon: AlertTriangle, color: 'red' }
                                    ].map(({ value, label, icon: Icon, color }) => (
                                        <button
                                            key={value}
                                            type="button"
                                            onClick={() => setFormData(prev => ({ ...prev, priority: value }))}
                                            className={`flex-1 flex flex-col md:flex-row items-center justify-center gap-2 px-2 py-3 md:px-4 border-2 rounded-xl transition-all ${formData.priority === value
                                                ? `border-${color}-500 bg-${color}-50 text-${color}-700`
                                                : 'border-border hover:border-border'
                                                }`}
                                            style={{
                                                borderColor: formData.priority === value
                                                    ? (color === 'blue' ? '#3b82f6' : color === 'amber' ? '#f59e0b' : '#ef4444')
                                                    : undefined,
                                                backgroundColor: formData.priority === value
                                                    ? (color === 'blue' ? '#eff6ff' : color === 'amber' ? '#fffbeb' : '#fef2f2')
                                                    : undefined,
                                                color: formData.priority === value
                                                    ? (color === 'blue' ? '#1d4ed8' : color === 'amber' ? '#b45309' : '#b91c1c')
                                                    : undefined
                                            }}
                                        >
                                            <Icon className="w-4 h-4" />
                                            <span className="font-medium text-xs md:text-sm">{label}</span>
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Content Editor */}
                            <div>
                                <label className="block text-sm font-medium text-foreground mb-2">
                                    Notice Content *
                                </label>
                                <RichTextEditor
                                    value={formData.content}
                                    onChange={handleContentChange}
                                    placeholder="Write your notice content here..."
                                />
                            </div>

                            {/* External Link */}
                            <div>
                                <label className="block text-sm font-medium text-foreground mb-2">
                                    <div className="flex items-center gap-2">
                                        <LinkIcon className="w-4 h-4" />
                                        External Link (Optional)
                                    </div>
                                </label>
                                <input
                                    type="url"
                                    name="external_link"
                                    value={formData.external_link}
                                    onChange={handleInputChange}
                                    placeholder="https://example.com"
                                    className="w-full px-4 py-3 border border-border rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent"
                                />
                            </div>

                            {/* Attachments */}
                            <div>
                                <label className="block text-sm font-medium text-foreground mb-2">
                                    <div className="flex items-center gap-2">
                                        <Paperclip className="w-4 h-4" />
                                        Attachments
                                    </div>
                                </label>

                                <div className="border-2 border-dashed border-border rounded-xl p-4">
                                    <input
                                        type="file"
                                        id="file-upload"
                                        multiple
                                        onChange={handleFileUpload}
                                        className="hidden"
                                    />
                                    <label
                                        htmlFor="file-upload"
                                        className="flex flex-col items-center justify-center cursor-pointer py-4"
                                    >
                                        {uploadingFile ? (
                                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                                        ) : (
                                            <>
                                                <Upload className="w-8 h-8 text-muted-foreground mb-2" />
                                                <span className="text-sm text-gray-600">Click to upload files</span>
                                                <span className="text-xs text-muted-foreground mt-1">PDF, DOC, Images, etc.</span>
                                            </>
                                        )}
                                    </label>

                                    {formData.attachments.length > 0 && (
                                        <div className="mt-4 space-y-2">
                                            {formData.attachments.map((file, index) => (
                                                <div
                                                    key={index}
                                                    className="flex items-center justify-between bg-muted rounded-lg p-3"
                                                >
                                                    <div className="flex items-center gap-3 overflow-hidden">
                                                        <Paperclip className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                                                        <span className="text-sm font-medium text-foreground truncate">
                                                            {file.file_name}
                                                        </span>
                                                    </div>
                                                    <button
                                                        type="button"
                                                        onClick={() => handleRemoveAttachment(index)}
                                                        className="p-1 text-red-500 hover:bg-red-50 rounded"
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Targeting Section */}
                    {activeSection === 'targeting' && (
                        <div className="space-y-6">
                            {/* Assignment Type */}
                            <div>
                                <label className="block text-sm font-medium text-foreground mb-3">
                                    Who should see this notice?
                                </label>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                    {[
                                        { value: 'all', label: 'Company-Wide', desc: 'All employees', icon: Users },
                                        { value: 'role', label: 'Role-Specific', desc: 'By position', icon: UserCheck },
                                        { value: 'individual', label: 'Specific Employees', desc: 'Select individuals', icon: UserCheck }
                                    ].map(({ value, label, desc, icon: Icon }) => (
                                        <button
                                            key={value}
                                            type="button"
                                            onClick={() => setFormData(prev => ({
                                                ...prev,
                                                assignment_type: value,
                                                assigned_roles: value !== 'role' ? [] : prev.assigned_roles,
                                                assigned_employee_ids: value !== 'individual' ? [] : prev.assigned_employee_ids
                                            }))}
                                            className={`p-4 rounded-xl border-2 text-left transition-all ${formData.assignment_type === value
                                                ? 'border-primary bg-primary/5'
                                                : 'border-border hover:border-border'
                                                }`}
                                        >
                                            <div className="flex items-center gap-3 mb-2 md:block md:mb-2">
                                                <Icon className={`w-5 h-5 ${formData.assignment_type === value ? 'text-primary' : 'text-muted-foreground'
                                                    }`} />
                                                <p className={`font-medium ${formData.assignment_type === value ? 'text-primary' : 'text-foreground'
                                                    }`}>{label}</p>
                                            </div>
                                            <p className="text-xs text-muted-foreground">{desc}</p>
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Role Selection */}
                            {formData.assignment_type === 'role' && (
                                <div>
                                    <label className="block text-sm font-medium text-foreground mb-3">
                                        Select Roles
                                    </label>
                                    {roles.length === 0 ? (
                                        <p className="text-muted-foreground text-sm">No roles found. Add employees with positions first.</p>
                                    ) : (
                                        <div className="grid grid-cols-2 lg:grid-cols-3 gap-2">
                                            {roles.map((role) => (
                                                <button
                                                    key={role}
                                                    type="button"
                                                    onClick={() => handleRoleToggle(role)}
                                                    className={`px-4 py-2 rounded-lg border text-sm font-medium transition-all ${formData.assigned_roles.includes(role)
                                                        ? 'border-primary bg-primary/10 text-primary'
                                                        : 'border-border hover:border-border text-foreground'
                                                        }`}
                                                >
                                                    {role}
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Employee Selection */}
                            {formData.assignment_type === 'individual' && (
                                <div>
                                    <label className="block text-sm font-medium text-foreground mb-3">
                                        Select Employees ({formData.assigned_employee_ids.length} selected)
                                    </label>
                                    {employees.length === 0 ? (
                                        <p className="text-muted-foreground text-sm">No employees found.</p>
                                    ) : (
                                        <div className="max-h-64 overflow-y-auto border border-border rounded-xl">
                                            {employees.map((employee) => (
                                                <button
                                                    key={employee.id}
                                                    type="button"
                                                    onClick={() => handleEmployeeToggle(employee.id)}
                                                    className={`w-full flex items-center gap-3 px-4 py-3 border-b border-border last:border-b-0 transition-colors ${formData.assigned_employee_ids.includes(employee.id)
                                                        ? 'bg-primary/5'
                                                        : 'hover:bg-muted'
                                                        }`}
                                                >
                                                    <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${formData.assigned_employee_ids.includes(employee.id)
                                                        ? 'border-primary bg-primary'
                                                        : 'border-border'
                                                        }`}>
                                                        {formData.assigned_employee_ids.includes(employee.id) && (
                                                            <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                                                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                                            </svg>
                                                        )}
                                                    </div>
                                                    <div className="flex-1 text-left">
                                                        <p className="font-medium text-foreground">
                                                            {employee.first_name} {employee.last_name}
                                                        </p>
                                                        <p className="text-xs text-muted-foreground">
                                                            {employee.position || 'No position'} • {employee.email}
                                                        </p>
                                                    </div>
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Settings Section */}
                    {activeSection === 'settings' && (
                        <div className="space-y-6">
                            {/* Popup Setting */}
                            <div className="flex flex-col md:flex-row md:items-center justify-between p-4 bg-purple-50 rounded-xl border border-purple-200 gap-4 md:gap-0">
                                <div className="flex items-center gap-3">
                                    <div className="p-2 bg-purple-100 rounded-lg">
                                        <Eye className="w-5 h-5 text-purple-600" />
                                    </div>
                                    <div>
                                        <p className="font-medium text-foreground">Show as Popup on Login</p>
                                        <p className="text-sm text-gray-600">Display this notice as a popup when employees log in</p>
                                    </div>
                                </div>
                                <label className="relative inline-flex items-center cursor-pointer">
                                    <input
                                        type="checkbox"
                                        name="show_popup_on_login"
                                        checked={formData.show_popup_on_login}
                                        onChange={handleInputChange}
                                        className="sr-only peer"
                                    />
                                    <div className="w-11 h-6 bg-muted peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-purple-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-card after:border-border after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-600"></div>
                                </label>
                            </div>

                            {/* Scheduled Date */}
                            <div>
                                <label className="block text-sm font-medium text-foreground mb-2">
                                    <div className="flex items-center gap-2">
                                        <Clock className="w-4 h-4" />
                                        Schedule for Later (Optional)
                                    </div>
                                </label>
                                <input
                                    type="datetime-local"
                                    name="scheduled_date"
                                    value={formData.scheduled_date}
                                    onChange={handleInputChange}
                                    className="w-full px-4 py-3 border border-border rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent"
                                />
                                <p className="text-xs text-muted-foreground mt-1">Leave empty to publish immediately</p>
                            </div>

                            {/* Expiry Date */}
                            <div>
                                <label className="block text-sm font-medium text-foreground mb-2">
                                    <div className="flex items-center gap-2">
                                        <Calendar className="w-4 h-4" />
                                        Expiry Date (Optional)
                                    </div>
                                </label>
                                <input
                                    type="datetime-local"
                                    name="expiry_date"
                                    value={formData.expiry_date}
                                    onChange={handleInputChange}
                                    className="w-full px-4 py-3 border border-border rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent"
                                />
                                <p className="text-xs text-muted-foreground mt-1">Notice will automatically hide after this date</p>
                            </div>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="px-4 py-3 md:px-6 md:py-4 border-t border-border bg-muted flex items-center justify-between safe-area-bottom">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 text-foreground hover:bg-muted rounded-lg font-medium transition-colors text-sm md:text-base"
                    >
                        Cancel
                    </button>
                    <div className="flex gap-2 md:gap-3">
                        <button
                            onClick={() => handleSubmit('draft')}
                            disabled={loading}
                            className="px-3 py-2 md:px-4 border border-border text-foreground hover:bg-gray-100 rounded-lg font-medium transition-colors disabled:opacity-50 text-sm md:text-base whitespace-nowrap"
                        >
                            Save Draft
                        </button>
                        {formData.scheduled_date ? (
                            <button
                                onClick={handleSchedule}
                                disabled={loading}
                                className="flex items-center gap-2 px-3 py-2 md:px-4 bg-primary hover:bg-primary-hover text-white rounded-lg font-medium transition-colors disabled:opacity-50 text-sm md:text-base whitespace-nowrap"
                            >
                                <Clock className="w-4 h-4" />
                                Schedule
                            </button>
                        ) : (
                            <button
                                onClick={() => handleSubmit('published')}
                                disabled={loading}
                                className="flex items-center gap-2 px-3 py-2 md:px-4 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50 text-sm md:text-base whitespace-nowrap"
                            >
                                {loading ? (
                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                ) : (
                                    <Bell className="w-4 h-4" />
                                )}
                                Publish
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default NoticeCreationModal;

